import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * The member guide, English source (4b step 4). Every number comes from `limits`; every claim
 * about who can read what follows the threat model (PLAN.md 4.12). Keep the Serbian text
 * (sr.tsx) in step with this one.
 */
export function GuideEn({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>How Vrelis works</h1>
      <p className="lead">
        Vrelis is a service for sending encrypted messages, time capsules, that can be read only from a moment you choose: by everyone in the pilot, or by
        one person. Before that moment nobody can read a capsule: not the person you wrote to, not the people who run Vrelis, not you.
      </p>
      <p className="warn">
        This is a closed pilot. Download the capsules you want to keep (see below), and keep your recovery phrase.
      </p>

      <h2>Why the lock can be trusted</h2>
      <p>
        The lock is not a promise by Vrelis. It is published cryptography plus a public time signal that Vrelis does not control. Here is what holds it,
        and what you would need to believe for it to fail.
      </p>

      <h3>drand: a public clock that signs the time</h3>
      <ul>
        <li>
          <Ext href="https://drand.love"><b>drand</b></Ext> is a network run by the{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext>, a group of independent organisations (among them Cloudflare, Protocol Labs, Kudelski
          Security, EPFL and University College London) that together publish a random, signed value every 3 seconds. The values are numbered: round 1,
          round 2, and so on, each for a known moment. Anyone can read them from several public relays; nobody can read a future one, because it does not
          exist yet.
        </li>
        <li>
          Each round is signed with threshold cryptography (<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">BLS signatures</Ext>): every organisation holds one share of a secret key, and a signature needs a
          majority of the shares. No single organisation, and no minority of them, can produce a round's signature ahead of its time. The network has run
          since 2020; the chain we use,{" "}
          <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext>, since 2023.
          Its public key is fixed inside our code, and every signal we receive is checked against it.
        </li>
        <li>
          <b>Time-lock encryption</b> (<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>, published by researchers from Protocol Labs and Cloudflare
          in 2023) turns that clock into a lock: a message is
          encrypted to a round number, and the round's signature, once published, is the only key that opens it. Until drand publishes it, that key exists
          nowhere in the world.
        </li>
      </ul>

      <h3>How Vrelis uses it</h3>
      <ul>
        <li>
          Your browser encrypts the text and the keys of your files to the drand round of the time you chose; Write shows the round number before you send.
          A private capsule is encrypted a second time to the recipient's key, so after the time only they can open it.
        </li>
        <li>
          Our server receives only the sealed result and stores it. It holds no key that opens a capsule: not before the time, and for a private capsule not
          after it either. A database leak would expose ciphertext and metadata, never a message.
        </li>
        <li>
          When the time comes, your browser (or the offline opener) fetches the round's signature from one of four public drand relays, verifies it
          against the fixed public key, and decrypts. Vrelis plays no part in that step.
        </li>
        <li>
          Every capsule is signed by its author's key (<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>, the same kind of key as Bitcoin
          and <Ext href="https://nostr.com">Nostr</Ext> use), so a changed capsule is detected. The key is made in your browser from your 12 words (
          <Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>) and never leaves it unencrypted.
        </li>
      </ul>

      <h3>If Vrelis disappears</h3>
      <ul>
        <li>
          The formats are open: a capsule is a <Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">Nostr event</Ext> (a{" "}
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">NIP-59 gift wrap</Ext> for a private one) whose content is an{" "}
          <Ext href="https://age-encryption.org">age</Ext> file with a <Ext href="https://github.com/drand/tlock-js">tlock</Ext> stanza. Any software that
          implements these open specifications can open your capsules; ours is not required.
        </li>
        <li>
          <b>Download</b> keeps capsules on your own disk, together with the offline opener: one HTML file that opens them on your computer, with no
          Vrelis server, using the required drand signal and, for a private capsule, your key. A complete export can be opened without vrelis.com if the
          required signal is available and you retain the files and keys it needs.
        </li>
        <li>
          If drand itself ever stopped before producing the required future signal, those capsules could remain locked permanently. Downloading them in
          advance does not remove that risk. A capsule whose opening date has passed still needs its exact verified signal: without that signal in your
          archive or available from a relay, the date alone does not let you open it offline. Keep complete exports and the keys they require.
        </li>
      </ul>

      <h3>What you would need to believe for it to fail</h3>
      <ul>
        <li>
          That a majority of the drand organisations collude to sign a round early. They are independent, in several countries, and the chain is public, so
          such a signature would be visible to everyone. This is the trust assumption of the whole system.
        </li>
        <li>
          That the web page lies. The page is code that Vrelis serves to your browser; whoever controls that code could read text before it is sealed, or your
          key after you unlock it. That is why capsules can be downloaded and opened offline, why fingerprints of keys are shown for you to compare outside
          Vrelis, and why a phone app that keeps the key off the web page is planned after the pilot.
        </li>
        <li>
          That today's cryptography is broken. Elliptic-curve cryptography, which both drand and your key use, may be broken by future{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">quantum computers</Ext>.
          For capsules meant to stay sealed for decades this is a real risk that the pilot does not solve.
        </li>
      </ul>

      <h2>Getting in</h2>
      <ol>
        <li>
          If the administrator approved your address, register with it directly; otherwise open your invitation link and use its address. Sign in with that Google account (Google only tells Vrelis your
          name and email), or with a password (we mail a link to confirm the address). Later you can add a passkey to sign in.
        </li>
        <li>
          Vrelis offers to make your <b>key</b> in your browser: your main signature, which signs what you send and opens private capsules sent to you. You
          can skip it and make it later under Account, Keys; without a key you read public capsules only. Before your <b>recovery phrase</b> (12 words that
          rebuild the key on any device) appears, you are asked to make sure nobody can see or record your screen.
        </li>
        <li>Write the 12 words on paper. You are asked to type three of them back, then to sign in again to confirm it is you: with your password or a passkey, or with Google for your very first key.</li>
        <li>
          Choose a PIN (at least {l.pinDigits} digits) and create a <Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">passkey</Ext> (fingerprint,
          face or your device PIN). Together they protect the key on this device
          and the copy Vrelis keeps for moving to a new device. If your browser cannot use a passkey this way, the key stays unprotected in that browser and
          there is no copy on the server; the phrase is then the only way to another device.
        </li>
      </ol>

      <h2>Your recovery phrase</h2>
      <ul>
        <li>Keep it on paper, somewhere safe. Do not photograph it and do not store it on a computer or in a password manager.</li>
        <li>
          Whoever has the 12 words <b>is</b> you: they can sign as you and open your private capsules. If they leak, use <b>Reset key</b> under Account,
          Keys: you type the phrase, sign in again, and the key is revoked for good; nobody can send with it or to it any more. You say since when it may have
          been stolen, and capsules sent with it from then on are marked "revoked key". Then you make a new key with a new phrase.
        </li>
        <li>
          Your earlier keys can go into a <b>keyring</b>, encrypted with your current key, so capsules sent to an old key still open without the old phrase.
          You can also keep the old phrase yourself and add it to the keyring later.
        </li>
        <li>
          If you lose a device: log in on another one and use your passkey and PIN (if your passkey syncs) or your phrase. If you forget the PIN: use the
          phrase. After {l.pinAttempts} wrong PINs the copy on the server is blocked; the phrase writes a new one.
        </li>
        <li>
          If you lose the phrase <b>and</b> every device with your key, your private capsules can never be opened, by anyone. An admin can only give you a
          new identity. Revoking a key needs the phrase too, so without it an admin revokes it for you.
        </li>
      </ul>

      <h2>Writing a capsule</h2>
      <ul>
        <li>
          <b>Everyone in the pilot</b> (public) or <b>one member</b> (private). A public capsule can be read by every member after its time; a private one only
          by the person you wrote to, and by you if you keep a copy (on by default).
        </li>
        <li>
          Every capsule needs a <b>subject</b>. It is never encrypted: whoever sees the capsule reads it at once, and so does our server.
        </li>
        <li>
          An <b>intro</b> is optional: up to {l.introChars} characters that can be read before the time, when the capsule is clicked. On a public capsule
          everyone reads it, our server too. On a private one only the recipient reads it, with their key; the server does not see it. The message and its
          files stay locked either way.
        </li>
        <li>
          Who sent a private capsule is sealed to the recipient's key: they see it as soon as they open the capsule with their key, long before its time.
          Whoever has the capsule without that key sees only the subject and the time. Our server knows which account sent it. It tells only the recipient, encrypted to their key, so the app can check that the key that signed the capsule belongs to that account; otherwise it shows "Unverified sender" instead of a name.
        </li>
        <li>
          <b>Discreet</b> (private capsules): the recipient's lists show "Discreet sender" instead of your name until they click the capsule, so nobody
          looking over their shoulder sees who writes to them. The recipient can still see it is you, before the time.
        </li>
        <li>
          Choose when it opens: in 5 minutes, an hour, a day, a week, a year, or a date up to the year 2125. The real opening time is shown before you send; it
          is exact to a few seconds.
        </li>
        <li>
          Text: up to {l.publicText} for a public capsule, {l.privateText} for a private one. Files: up to {l.files} files and, in total, {l.fileBytes} on Free or {l.planFileBytes} with Legacy or Start. Photos
          are made smaller and lose their metadata, including location.
        </li>
        <li>
          <b>Seal and send</b> asks for your PIN and passkey once, to sign the capsule. After that nobody can change it, and nobody can read it before its
          time.
        </li>
        <li>
          Before you write to someone privately, check their key: Write shows its fingerprint (16 characters). If their key changed since this browser last
          saw it, Write warns you and waits until you confirm with them, outside Vrelis, that the new key is really theirs.
        </li>
      </ul>

      <h2>Reading</h2>
      <ul>
        <li>
          <b>Feed</b>: public capsules. <b>Inbox</b>: private capsules to you. <b>Sent</b>: yours. <b>Watching</b>: public capsules you wait for. Follow an
          author to see only their capsules.
        </li>
        <li>
          <b>Hide</b> a member: their public capsules leave your Feed, on every device; nothing changes for them. <b>Block</b> a member: they can no
          longer put private capsules into your Inbox (Write tells them before they send); you can still follow them and read their public capsules.
          Their earlier capsules move to Inbox, Blocked, and still open. Both are on a person's menu and on the People page, with who follows you.
          Vrelis vouches only for what it receives and delivers: nobody can stop someone from encrypting to your public key outside Vrelis.
        </li>
        <li>
          A private capsule shows nothing until you open it: <b>Open</b> asks for your PIN, then shows who sent it and when it opens, and the text once that
          time has come.
        </li>
        <li>
          Your key stays locked; each action that needs it asks for the PIN. To read several capsules in a row, use <b>Unlock for … minutes</b> in the bar at
          the top. <b>Lock</b> hides everything private at once; leaving the page does too.
        </li>
        <li>
          Under <a href="/account">Account</a>, Privacy: "Show senders in my lists only after I click a capsule" treats every private capsule as discreet,
          on every device.
        </li>
        <li>
          Lists show 50 capsules at a time; <b>Show more</b> adds the next ones. Vrelis filters and sorts them (by opening time or by when it received
          them), so a filter or a group sends Vrelis the ids of the members you chose, never the group or its name. Filtering your Inbox by sender needs
          your key, leaves out discreet capsules, and is off while senders show only after a click.
        </li>
        <li>
          <b>Search</b> looks at subjects, the intro of public capsules, who wrote a public capsule and whom you sent to. It never looks at opened text
          (only your device has it) or at who sent you a private capsule.
        </li>
      </ul>

      <h2>Reply envelope, CC and Correspondence</h2>
      <ul>
        <li>
          A private message can carry a <b>reply envelope</b>: you ask each recipient for one private reply back to you, sealed to your key.
          They can reply even if they do not follow you. You choose whether every reply opens at a time you set (after that time no reply is
          accepted) or at a time the recipient picks. You pay for the capsule and for the reply up front: 2 stamps per recipient instead of 1. A recipient's first reply costs them nothing; a second one costs them a stamp, and you see both.
        </li>
        <li>
          <b>CC</b>: a private message to one member can go to more members at once, and everyone listed sees who else received it. The list is
          sealed inside the message with the sender's signature; our server never sees it. A group send without CC stays blind: nobody sees
          who else got it.
        </li>
        <li>
          Messages with a reply envelope and the replies to them live on the <b>Correspondence</b> page, not in Inbox or Sent: under each
          request you see every recipient's replies in order, locked until their time, and who paid for each. Reply goes to the sender only.
        </li>
      </ul>

      <h2>Keeping capsules without Vrelis</h2>
      <ul>
        <li>
          <b>Download</b> on a capsule saves it as a file; with attachments it is a .zip with the files and the offline opener. Select several and use{" "}
          <b>Download selected</b> for one archive ("Select all" takes the capsules shown).
        </li>
        <li>
          The offline opener is one HTML file (<a href="/account">Account</a>, or inside the .zip). It opens your capsules on your own computer, private ones with your phrase. It
          never contacts vrelis.com; it only asks the public drand time service for the opening signal if the file does not hold it yet.
        </li>
        <li>
          "Include opened text and files" puts the opened text and files in the download unencrypted: anyone with the file can read them.
        </li>
      </ul>

      <h2>This device</h2>
      <ul>
        <li>
          <b>Lock</b>: removes the key from memory. <b>Log out</b>: ends this session; the key stays on this device. <b>Log out everywhere</b>:
          ends your sessions on every device. <b>Forget this device</b>: deletes your key and everything Vrelis stored in this browser.
        </li>
        <li>A login lasts {l.sessionDays} days.</li>
      </ul>

      <h2>What Vrelis can and cannot see</h2>
      <ul>
        <li>
          <b>Vrelis cannot read</b> a capsule before its time, or a private capsule ever (it has no key to it), or your key, PIN or phrase.
        </li>
        <li>
          <b>Vrelis does see</b>: your name and email (the owner and the admins see the email), who sends a capsule and to whom, when it was sent, when a public capsule
          opens, the sizes, and that a capsule has files. After its time a public capsule can be read by every member, and so by Vrelis too.
        </li>
        <li>
          The keyring and your key copies are stored only encrypted; Vrelis cannot open them.
        </li>
        <li>
          A signature proves which key wrote a capsule. A capsule marked "revoked key" was signed by a key its owner later reported stolen. That the key belongs to a person rests on the member list and on the name the sender put into the
          capsule. Check fingerprints with people you write to privately.
        </li>
      </ul>

      <h2>Plans and stamps</h2>
      <ul>
        <li>
          One <b>stamp</b> pays one delivery to one recipient. Your own copy is free. Free gives {l.freeStamps} stamps a month, {l.freeStorage} of storage and {l.fileBytes} of files per message; Legacy
          ({l.legacyStamps} stamps a month, {l.planStorage}, {l.planFileBytes}) was given to the first members; Start would give {l.planStamps} stamps a month. The stamps of a month do not carry over, and they come back on the
          first day of the next month (UTC). <b>Billing</b> shows what you have, what is left and what is planned.
        </li>
        <li>
          For now nothing is charged and nothing can be bought: the plans are a preview, and an admin turns a request on by hand. What you already sent stays readable by you and by the people you sent it to, whatever
          happens to your plan or your stamps.
        </li>
        <li>
          The copy for yourself is optional and on by default. If you turn it off, only the recipient can ever read that message: you will not be able to read it yourself, not even after it opens.
        </li>
      </ul>

      <h2>Limits of the pilot</h2>
      <ul>
        <li>
          At most {l.daily} deliveries per day (your own copy is free; a capsule to a group counts one per member). Uploads: {l.uploadDaily} and {l.unusedFiles} unused files per 24 hours;
          files not used in a capsule within 24 hours are deleted.
        </li>
        <li>Sign in with Google, a password or a passkey. No payments, no public network: capsules stay inside the pilot.</li>
        <li>
          Inviting an address that already has an account sends that member a request to connect instead; if they accept, you follow each other. Your
          screen looks the same either way, so nobody can use invites to find out who is a member.
        </li>
      </ul>

      <h2>For developers</h2>
      <p>
        Services can read and send capsules through the <a href="/developers">Vrelis API</a>. A service gets its own account through a service invite from
        an admin; to everyone else it is an ordinary member. It makes its own key and signs every capsule itself; its API tokens can read and send, but
        cannot manage keys or sign-in methods. You can block or hide a service like anyone else.
      </p>

      <h2>What we ask of pilot members</h2>
      <ol>
        <li>Send one public capsule that opens in an hour, and one private capsule to another member that opens in a day.</li>
        <li>Read the public capsules after they open, and try to read one before: you should not be able to.</li>
        <li>One of you: set up your key on a second device and read your Inbox there.</li>
        <li>One of you: hide another member and check that their capsules disappear from your Feed only.</li>
      </ol>

      <h2>Problems</h2>
      <p>
        Use <b>Report a problem</b> at the bottom of every page, or write to {link}. Say what you did, what you expected and what happened; a screenshot helps.
        The service, its limits and responsibilities are described in <a href="/terms">Terms of use</a>.
      </p>
    </>
  );
}
